import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { MapPin, Package, CheckCircle, Star, Heart } from 'lucide-react';
import { useTranslation } from '@/contexts/LanguageContext';
import LazyImage from '@/components/LazyImage';

const conditionColors = {
  neuf: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  occasion: 'bg-amber-100 text-amber-700 border-amber-200',
  reconditionne: 'bg-purple-100 text-purple-700 border-purple-200',
};

const PLACEHOLDER_IMG = 'https://images.unsplash.com/photo-1631856956334-35db20cb7748?w=400&h=300&fit=crop';

export default function ListingCard({ listing, isFavorited, onToggleFavorite }) {
  const { t: tCond } = useTranslation('conditions');
  const { tc, lang } = useTranslation('common');
  const locale = lang === 'nl' ? 'nl-NL' : 'fr-FR';

  const imgSrc = listing.images && listing.images.length > 0
    ? (listing.images[0].startsWith('http') ? listing.images[0] : `${process.env.REACT_APP_BACKEND_URL}${listing.images[0]}`)
    : PLACEHOLDER_IMG;
  const isBoosted = listing.active_boosts?.badge || listing.active_boosts?.search_top || listing.active_boosts?.homepage;

  return (
    <Link
      to={`/annonces/${listing.id}`}
      className={`listing-card group relative bg-white dark:bg-neutral-800 border rounded-lg overflow-hidden hover:shadow-md transition-shadow duration-200 ${isBoosted ? 'border-[#F25C05]/30 ring-1 ring-[#F25C05]/10' : 'border-neutral-200 dark:border-neutral-700'}`}
      data-testid={`listing-card-${listing.id}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
        <LazyImage
          src={imgSrc}
          alt={listing.title}
          className="w-full h-full"
        />
        <div className="absolute top-2 left-2 flex gap-1.5">
          {listing.active_boosts?.badge && (
            <Badge className="bg-[#F25C05] text-white text-[10px] font-semibold px-2 py-0.5" data-testid="boost-badge">
              <Star className="w-2.5 h-2.5 mr-0.5 fill-current" /> {tc('topAd')}
            </Badge>
          )}
        </div>
        {listing.quantity > 1 && (
          <div className="absolute top-2 right-2">
            <Badge className="bg-[#1F3F72] text-white text-[10px] px-2 py-0.5">
              <Package className="w-3 h-3 mr-1" /> x{listing.quantity}
            </Badge>
          </div>
        )}
        {onToggleFavorite && (
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFavorite(listing.id); }}
            className={`absolute ${listing.quantity > 1 ? 'top-10' : 'top-2'} right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all ${isFavorited ? 'bg-white text-red-500' : 'bg-white/80 text-neutral-400 hover:text-red-400'}`}
            data-testid={`favorite-btn-${listing.id}`}
          >
            <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
          </button>
        )}
        {listing.active_boosts?.search_top && (
          <div className="absolute bottom-2 left-2">
            <Badge className="bg-blue-600/90 text-white text-[10px] px-2 py-0.5" data-testid="sponsored-badge">
              {tc('sponsored')}
            </Badge>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3.5">
        <h3 className="font-heading font-semibold text-sm text-neutral-900 dark:text-neutral-100 line-clamp-2 leading-tight mb-1.5 group-hover:text-[#1F3F72] dark:group-hover:text-[#F25C05] transition-colors">
          {listing.title}
        </h3>

        <p className="text-lg font-bold text-[#F25C05] mb-0.5">
          {listing.price?.toLocaleString(locale, { style: 'currency', currency: 'EUR' })}
          <span className="text-[10px] font-semibold text-neutral-400 ml-1">{tc('vatExcl')}</span>
          <span className="text-xs font-normal text-neutral-500 ml-1">/ {tc('unit')}</span>
        </p>
        {listing.quantity > 1 && (
          <p className="text-xs text-neutral-400 mb-1.5">{listing.quantity} {tc('available')}</p>
        )}
        {(!listing.quantity || listing.quantity <= 1) && <div className="mb-2" />}

        <div className="flex items-center gap-3 text-xs text-neutral-500 dark:text-neutral-400">
          {listing.location && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {listing.location}
            </span>
          )}
        </div>

        {listing.company_name && (
          <div className="flex items-center gap-1.5 mt-2">
            <p className="text-xs text-neutral-400 truncate">{listing.company_name}</p>
            {listing.seller_verified && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-600 shrink-0" data-testid="listing-verified-badge">
                <CheckCircle className="w-3 h-3" />
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}
